import { NavLink, useNavigate } from "react-router-dom";

import { PanelOutlet } from "../../components/PanelOutlet";
import { Button, CardIcon, ChartIcon, IdCardIcon, LogOutIcon, Logo, ScanIcon, SettingsIcon } from "../../components/ui";
import { useAuth } from "../../lib/auth";
import { currentSlug } from "../../lib/host";
import { useVenue } from "../../lib/queries";
import { useMobile } from "../../lib/useMediaQuery";

const NAV = [
  { to: "/", label: "Karty", end: true, Icon: CardIcon },
  { to: "/tiers", label: "Poziomy", end: false, Icon: IdCardIcon },
  { to: "/stats", label: "Statystyki", end: false, Icon: ChartIcon },
  { to: "/settings", label: "Ustawienia", end: false, Icon: SettingsIcon },
];

/**
 * Panel shell - 232px sidebar on desktop; sticky header + tab strip + bottom
 * scanner bar under 900px. The scanner is a different job on a different
 * device, so it never hides among the tabs.
 */
export function VenueLayout() {
  const { user, logout } = useAuth();
  const venue = useVenue();
  const navigate = useNavigate();
  const mobile = useMobile();
  const name = venue.data?.name ?? currentSlug() ?? "";

  const openScanner = () => window.location.assign("/scanner");

  if (mobile) {
    return (
      <div className="flex min-h-full flex-col">
        <header className="sticky top-0 z-20 border-b border-line-soft bg-[rgba(8,8,12,0.82)] backdrop-blur-[14px]">
          <div className="flex h-14 items-center justify-between px-4">
            <div className="flex items-center gap-2.5">
              <Logo size={18} />
              <span className="h-4 w-px bg-line-strong" />
              <span className="max-w-[44vw] truncate text-[13px] font-semibold">{name}</span>
            </div>
            <Button variant="ghost" size="sm" className="text-muted" onClick={() => void logout()}>
              Wyloguj
            </Button>
          </div>
          <nav className="grid grid-cols-4 px-2">
            {NAV.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.end}
                className={({ isActive }) =>
                  `relative flex h-[42px] items-center justify-center rounded-sm text-[13px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:shadow-glow ${
                    isActive ? "text-ink" : "text-muted"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {tab.label}
                    {isActive && <span className="absolute -bottom-px left-2.5 right-2.5 h-0.5 rounded-sm bg-accent-2" />}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </header>

        <main className="min-w-0 flex-1 px-4 pb-[104px] pt-5">
          <PanelOutlet />
        </main>

        <div className="fixed inset-x-0 bottom-0 z-20 px-4 pb-[calc(12px+env(safe-area-inset-bottom))] pt-2.5 [background:linear-gradient(180deg,rgba(8,8,12,0)_0%,rgba(8,8,12,0.95)_40%)]">
          <Button variant="secondary" size="lg" block icon={<ScanIcon size={18} />} onClick={openScanner} className="h-[50px] border-line-strong bg-panel-2">
            Otwórz skaner
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid min-h-full grid-cols-[232px_minmax(0,1fr)]">
      <aside className="sticky top-0 flex h-screen flex-col gap-1 border-r border-line-soft px-3.5 py-5">
        <div className="px-2.5 pb-[22px] pt-1">
          <Logo size={20} />
        </div>

        {NAV.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              `flex h-9 items-center gap-2.5 rounded-sm px-2.5 text-[13px] font-medium transition duration-150 focus-visible:outline-none focus-visible:shadow-glow ${
                isActive ? "bg-glass-2 text-ink" : "text-muted hover:text-ink"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span className={isActive ? "text-accent-2" : "text-faint"}>
                  <tab.Icon size={16} />
                </span>
                {tab.label}
              </>
            )}
          </NavLink>
        ))}

        {/* Pinned to the bottom, just above the venue card: the scanner is a
            different job on a different device, not a fifth nav item. */}
        <Button variant="secondary" block icon={<ScanIcon size={18} />} onClick={openScanner} className="mt-auto h-[42px] justify-start">
          Otwórz skaner
        </Button>

        <div className="mt-2.5 grid gap-2.5 rounded-[12px] border border-line bg-glass p-3">
          <div className="flex items-center gap-2.5">
            <span aria-hidden="true" className="tier-gold grid h-8 w-8 shrink-0 place-items-center rounded-[9px] text-xs font-bold tracking-[0.02em] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)]">
              {name.slice(0, 2).toUpperCase()}
            </span>
            <div className="min-w-0">
              <div className="truncate text-[13px] font-semibold">{name}</div>
              <div className="truncate text-[11px] text-muted">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 border-t border-line-soft pt-2">
            <Button variant="ghost" size="sm" className="flex-1 justify-start px-2 text-muted" onClick={() => navigate("/settings#password")}>
              Zmień hasło
            </Button>
            <Button variant="ghost" size="sm" className="w-8 px-0 text-muted" aria-label="Wyloguj" title="Wyloguj" onClick={() => void logout()}>
              <LogOutIcon size={16} />
            </Button>
          </div>
        </div>
      </aside>

      <main className="mx-auto w-full min-w-0 max-w-[1100px] px-9 pb-16 pt-7">
        <PanelOutlet />
      </main>
    </div>
  );
}
